import HiddenInput from "@/components/form/components/HiddenInput";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, DatePicker, Form, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateClientAgreement, useUpdateClientAgreement } from "../../../../common/api/queries/clientAgreements";
import { ModalSizes } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { ClientAgreementType } from "../../enums";
import { ClientAgreement, CreateUpdateClientAgreement } from "../../types";
import { convertClientAgreementToCreateUpdateObject } from "../../utils";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  agreement?: ClientAgreement;
  clientId: UUID;
};

export const ClientAgreementModalForm = ({ isOpen, onCancel, agreement, clientId }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateClientAgreement>();

  const clientAgreement = useCreateClientAgreement();
  const updateClientAgreement = useUpdateClientAgreement();

  useEffect(() => {
    if (isOpen && agreement) {
      const formData = convertClientAgreementToCreateUpdateObject(agreement);
      form.setFieldsValue(formData);
    }
  }, [isOpen, agreement]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (agreement) {
        updateClientAgreement.mutate(
          {
            id1: clientId,
            id2: agreement.id,
            object: values,
          },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "client.agreement.attrs", error.violations),
          },
        );
      } else {
        clientAgreement.mutate(
          {
            id: clientId,
            object: values,
          },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "client.agreement.attrs", error.violations),
          },
        );
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      title={agreement ? t("client.agreement.titles.updateAgreement") : t("client.agreement.titles.addAgreement")}
      afterClose={() => form.resetFields()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={clientAgreement.isPending || updateClientAgreement.isPending}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="clientAgreementForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="type"
          label={t("client.agreement.enums.clientAgreementType._label")}
          rules={[validations.notNull]}
        >
          <Select
            {...selectStandardProps}
            options={Object.values(ClientAgreementType).map((type) => ({
              value: type,
              label: t(`client.agreement.enums.clientAgreementType.${type}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="date"
          label={t("client.agreement.attrs.date")}
          rules={[validations.notNull, validations.notFuture]}
          {...datePickerFormItemProps}
        >
          <DatePicker {...datePickerStandardProps} maxDate={dayjs()} />
        </Form.Item>

        <Form.Item name="active" valuePropName="checked" rules={[validations.none]} initialValue={true} noStyle>
          <Checkbox>{t("client.agreement.attrs.active")}</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};
